import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import { listPassportReasons, createPassportReason, updatePassportReason, deletePassportReason } from '../../api/passportRegister';
import { pushToast } from '../../components/ui/toastBus';

const ACTION_LABEL = { collect: 'Collect', give: 'Give' };

function ReasonFormModal({ open, onClose, reason }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [action, setAction] = useState('collect');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(reason?.name || '');
    setAction(reason?.action || 'collect');
    setIsActive(reason?.is_active ?? true);
    setError('');
  }, [reason]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), action, is_active: isActive };
      return reason ? updatePassportReason(reason.id, payload) : createPassportReason(payload);
    },
    onSuccess: () => {
      pushToast({ type: 'success', message: reason ? 'Reason updated.' : 'Reason created.' });
      queryClient.invalidateQueries({ queryKey: ['passport-reasons'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not save reason.'),
  });

  const handleSave = () => {
    if (!name.trim()) {
      setError('Reason is required.');
      return;
    }
    setError('');
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={reason ? 'Edit passport reason' : 'New passport reason'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select label="Applies to" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="collect">Collect passport</option>
          <option value="give">Give passport</option>
        </Select>
        <Input label="Reason" value={name} onChange={(e) => setName(e.target.value)} />
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active (offered when collecting or giving a passport)
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

export default function PassportReasonMasters() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reasonsQuery = useQuery({
    queryKey: ['passport-reasons', { include_inactive: true }],
    queryFn: () => listPassportReasons({ include_inactive: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: deletePassportReason,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Reason deleted.' });
      queryClient.invalidateQueries({ queryKey: ['passport-reasons'] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({ type: 'error', message: err.response?.data?.message || 'Could not delete reason.' });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: 'name', label: 'Reason' },
    { key: 'action', label: 'Applies to', render: (row) => <Badge tone={row.action === 'collect' ? 'amber' : 'purple'}>{ACTION_LABEL[row.action]}</Badge> },
    { key: 'is_active', label: 'Status', render: (row) => <Badge tone={row.is_active ? 'green' : 'slate'}>{row.is_active ? 'Active' : 'Inactive'}</Badge> },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="masters.manage">
            <button
              onClick={() => {
                setFormTarget(row);
                setFormOpen(true);
              }}
              className="text-primary hover:underline"
            >
              Edit
            </button>
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Passport Reasons"
        description="Reasons staff pick from every time a passport is collected from, or given to, a candidate."
        actions={
          <Can perm="masters.manage">
            <Button
              onClick={() => {
                setFormTarget(null);
                setFormOpen(true);
              }}
            >
              New Reason
            </Button>
          </Can>
        }
      />

      <Table columns={columns} data={reasonsQuery.data || []} isLoading={reasonsQuery.isLoading} emptyMessage="No passport reasons yet." />

      {formOpen && <ReasonFormModal open={formOpen} onClose={() => setFormOpen(false)} reason={formTarget} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete passport reason"
        message={`Delete "${deleteTarget?.name}"? Past passport movements keep the text they were saved with.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
